import React from 'react';
import {
  Popover,
  Box,
  Typography,
  Chip,
  Button,
  Divider,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import { WorkItem } from '../workitems/workItemTypes';
import { ConflictWarning } from './calendarTypes';
import { CalendarConflictWarning } from './CalendarConflictWarning';
import { deleteAppointment } from '../../services/appointmentDataService';

interface AppointmentDetailPopoverProps {
  anchorEl: HTMLElement | null;
  workItem: WorkItem | null;
  conflicts: ConflictWarning[];
  onClose: () => void;
  onEdit?: (workItem: WorkItem) => void;
}

export const AppointmentDetailPopover: React.FC<AppointmentDetailPopoverProps> = ({
  anchorEl,
  workItem,
  conflicts,
  onClose,
  onEdit,
}) => {
  const open = Boolean(anchorEl) && Boolean(workItem);

  if (!workItem) return null;

  const startTime = workItem.startAt
    ? new Date(workItem.startAt).toLocaleTimeString('en-AU', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : 'N/A';

  const endTime = workItem.endAt
    ? new Date(workItem.endAt).toLocaleTimeString('en-AU', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : '';

  const dateLabel = workItem.startAt
    ? new Date(workItem.startAt).toLocaleDateString('en-AU', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      })
    : '';

  const customerLabel = workItem.customerName || workItem.customerBusinessName || 'No customer';

  const handleCancel = async () => {
    if (!window.confirm('Cancel this appointment?')) return;
    try {
      await deleteAppointment(workItem.guid);
      onClose();
    } catch (err) {
      console.error('Failed to cancel appointment:', err);
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Box sx={{ p: 2, minWidth: 280, maxWidth: 360 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {workItem.title}
        </Typography>

        {workItem.appointmentType && (
          <Chip
            label={workItem.appointmentType}
            size="small"
            sx={{ mb: 1 }}
          />
        )}

        <CalendarConflictWarning conflicts={conflicts} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <EventIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {dateLabel} {startTime}{endTime ? ` – ${endTime}` : ''}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <PersonIcon fontSize="small" color="action" />
          <Typography variant="body2">{customerLabel}</Typography>
        </Box>

        {workItem.userFullName && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Assigned: {workItem.userFullName}
          </Typography>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Box sx={{ display: 'flex', gap: 1 }}>
          {onEdit && (
            <Button size="small" variant="outlined" onClick={() => onEdit(workItem)}>
              Edit
            </Button>
          )}
          <Button size="small" color="error" onClick={handleCancel}>
            Cancel Appointment
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};
